import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const FeatureSlideshow = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [imageLoaded, setImageLoaded] = useState({});

  // Local slideshow data with images from public/assets/images folder
  const slides = [
    {
      id: 1,
      image: '/assets/images/feature-01-schedule-1766970255504.png',
      alt: 'Schedule feature showing personalized time blocks and daily organization',
      title: 'Master Your Daily Schedule',
      description: 'Create personalized time blocks that fit your lifestyle. Organize your day with precision so that you can tame your day.',
      imagePosition: 'top' // Crop to show TOP of screenshot
    },
    {
      id: 2,
      image: '/assets/images/feature-04-woman1-1766970554816.png',
      alt: 'Woman using tablet for planning and productivity',
      title: 'Planning Made Simple',
      description: 'Take control of your productivity anywhere, anytime. Join users who have transformed their daily routine with StillFlow.',
      imagePosition: 'center' // Keep centered for people
    },
    {
      id: 3,
      image: '/assets/images/feature-02-tasks-1766970418364.png',
      alt: 'Tasks page showing organized to-do lists and task management',
      title: 'Stay on Top of Your Tasks',
      description: 'Organize, prioritize, and track your to-dos effortlessly. Turn overwhelming task lists into manageable daily achievements.',
      imagePosition: 'top' // Crop to show TOP of screenshot
    },
    {
      id: 4,
      image: '/assets/images/feature-05-man1-1766970665396.png',
      alt: 'Professional man using tablet for project management and planning',
      title: 'Professional Planning for Professionals',
      description: 'Whether you\'re managing projects or personal goals, StillFlow adapts to your workflow and helps you stay focused on what matters most.',
      imagePosition: 'center' // Keep centered for people
    },
    {
      id: 5,
      image: '/assets/images/feature-03-journal-1766970499026.png',
      alt: 'Journal feature for daily reflection and habit tracking',
      title: 'Reflect & Grow Daily',
      description: 'Capture thoughts, track progress, and build meaningful habits. Your digital journal for personal growth and mindful planning.',
      imagePosition: 'top' // Crop to show TOP of screenshot
    },
    {
      id: 6,
      image: '/assets/images/feature-06-woman2-1766970732021.png',
      alt: 'Woman using tablet with organized planner interface',
      title: 'Your Life, Perfectly Organized',
      description: 'Experience the peace of mind that comes with having everything in its place. Start your organized life today with StillFlow.',
      imagePosition: 'center' // Keep centered for people
    }
  ];

  // Auto-advance slides every 3 seconds
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides?.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [isPlaying, slides?.length]);

  // Handle navigation
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides?.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides?.length) % slides?.length);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Handle image load
  const handleImageLoad = (index) => {
    setImageLoaded(prev => ({ ...prev, [index]: true }));
  };

  // Handle CTA button clicks
  const handleGetStarted = () => {
    navigate('/register');
  };

  const handleLearnMore = () => {
    navigate('/showcase');
  };

  return (
    <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-6 h-full flex flex-col">
      {/* Overall Header */}
      <div className="mb-6 text-center">
        <h2 className="font-heading text-3xl font-bold text-white mb-2 drop-shadow-lg">
          Better plan. Better day. Better life.
        </h2>
        <p className="font-body text-white/90 text-sm drop-shadow">
          Discover what makes StillFlow your perfect planning companion
        </p>
      </div>
      {/* Slideshow Container */}
      <div className="flex-1 flex flex-col">
        {/* Image Display Area */}
        <div className="relative bg-white/5 rounded-xl overflow-hidden mb-4 flex-1 min-h-[300px]">
          {slides?.map((slide, index) => (
            <div
              key={slide?.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              {/* Image Loading Skeleton */}
              {!imageLoaded?.[index] && (
                <div className="absolute inset-0 bg-white/10 animate-pulse flex items-center justify-center">
                  <Icon name="Image" size={48} className="text-white/30" />
                </div>
              )}
              
              {/* Actual Image */}
              <AppImage
                src={slide?.image}
                alt={slide?.alt}
                className={`w-full h-full object-cover transition-opacity duration-300 ${
                  imageLoaded?.[index] ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ objectPosition: slide?.imagePosition || 'center' }}
                onLoad={() => handleImageLoad(index)}
              />
            </div>
          ))}

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-3 transition-all duration-300 hover:scale-110 z-20"
            aria-label="Previous slide"
          >
            <Icon name="ChevronLeft" size={20} className="text-white" />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-3 transition-all duration-300 hover:scale-110 z-20"
            aria-label="Next slide"
          >
            <Icon name="ChevronRight" size={20} className="text-white" />
          </button>

          {/* Play/Pause Control */}
          <button
            onClick={togglePlayPause}
            className="absolute bottom-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 rounded-full p-2 transition-all duration-300 hover:scale-110 z-20"
            aria-label={isPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            <Icon 
              name={isPlaying ? 'Pause' : 'Play'} 
              size={16} 
              className="text-white" 
            />
          </button>
        </div>

        {/* Feature Description */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-4 min-h-[120px] flex flex-col justify-center">
          <h3 className="font-heading text-xl font-semibold text-white mb-2 drop-shadow-lg">
            {slides?.[currentSlide]?.title}
          </h3>
          <p className="font-body text-white/90 text-sm drop-shadow mb-4">
            {slides?.[currentSlide]?.description}
          </p>
          
          {/* CTA Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleGetStarted}
              className="flex-1 py-2.5 px-4 bg-white hover:bg-white/90 text-primary font-body text-sm font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center space-x-2"
            >
              <Icon name="Sparkles" size={16} />
              <span>Get Started</span>
            </button>
            
            <button
              onClick={handleLearnMore}
              className="flex-1 py-2.5 px-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white font-body text-sm font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center space-x-2"
            >
              <Icon name="ArrowRight" size={16} />
              <span>Learn More</span>
            </button>
          </div>
        </div>

        {/* Slide Indicators */}
        <div className="flex justify-center items-center space-x-2 mb-3">
          {slides?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide
                  ? 'w-8 h-3 bg-white' :'w-3 h-3 bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Slide Counter */}
        <div className="text-center">
          <span className="font-body text-xs text-white/70">
            {currentSlide + 1} / {slides?.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default FeatureSlideshow;